import { useLocation } from "react-router";

import { DESTINATIONS } from "@/lib/destinations";

/**
 * Routes reachable through the shell but not one of the nine nav
 * destinations — `/status` (Story 2.1) and Story 2.2's first two children
 * under Settings. Kept separate from `DESTINATIONS` deliberately: that
 * table drives the rail/bottom-bar/sheet nav, and none of these have a nav
 * item anywhere, so they don't belong in it.
 */
const EXTRA_ROUTE_LABELS: Record<string, string> = {
  "/status": "Status",
  "/settings/users": "Users",
};

/**
 * Each surface announces itself on navigation (EXPERIENCE.md § Accessibility
 * Floor: "Properties, list, 14 properties" — this story has no list counts
 * yet, so it announces the destination name alone). A client-side route
 * change does not fire a browser navigation event a screen reader picks up
 * on its own, so this is the one `aria-live` region every route change
 * updates; `DestinationPlaceholder`/`Status` additionally set
 * `document.title` per route so the browser tab and any assistive tech
 * reading it agree.
 */
export function RouteAnnouncer() {
  const location = useLocation();
  const destination = DESTINATIONS.find((d) => d.path === location.pathname);
  // `/settings/users/:id` carries a real id in the URL, so it can't be a
  // static map key — matched by prefix instead. The record page itself
  // (`UserRecord`, Story 2.5) sets `document.title` to the actual person's
  // name once it loads; this announcement stays generic on purpose rather
  // than reaching into another component's fetched data.
  const isUserRecordPage =
    location.pathname.startsWith("/settings/users/") && location.pathname !== "/settings/users/";
  const label =
    destination?.label ?? EXTRA_ROUTE_LABELS[location.pathname] ?? (isUserRecordPage ? "User record" : undefined);
  const announcement = label ? `${label}, PropDesk` : undefined;

  return (
    <div role="status" aria-live="polite" className="sr-only">
      {announcement}
    </div>
  );
}
